import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { observer } from 'mobx-react';
import { Outlet, useLocation, useNavigate, useOutletContext, useSearchParams } from 'react-router-dom';
import { Button, Grid, Space, Tabs, message } from 'antd';
import { ExperimentOutlined, PlayCircleOutlined, StarOutlined } from '@ant-design/icons';
import { PageHeader } from '@/components/PageHeader';
import { AgentPickerDrawer } from '@/components/AgentPickerDrawer';
import { useStore } from '@/stores';
import { getAgentById } from '@/api/agents';
import { getAgentModel, type AgentModelState } from '@/api/agentModel';
import { enqueueValidationRun } from '@/api/validationRuns';
import { getEvaluationWorklist, getLastEvaluatedAgent, type EvaluationWorklist } from '@/api/evaluationWorklist';
import { getScenarioReviewQueue } from '@/api/scenarioVersions';
import type { AgentDefinition } from '@/api/types';
import { RunTargetDrawer, type RunTargetRequest } from '@/features/validations/RunTargetDrawer';
import { FindingDrawer } from '@/features/validations/FindingDrawer';
import { EvaluationModelsDrawer } from '@/features/validations/EvaluationModelsDrawer';
import { GenerateScenariosDrawer } from '../GenerateScenariosDrawer';
import { ProposalsDrawer } from '../ProposalsDrawer';
import { evaluationPath } from './worklistWords';

// ─────────────────────────────────────────────────────────────────────────────
// The Scenario Intelligence workspace (/evaluation) — screen 01 and its tabs (plan §3, §5; mockup 01 rev 2).
//
// One destination, six tabs as nested routes: Overview, Coverage, Scenarios, Runs, Compare, Reviews. Scenarios, Runs,
// Compare and Reviews are the pages and drawers that already exist (the library, SI-03b's run pages, SI-07's Compare,
// screen 05's review queue); the old routes and their bookmarks keep working beside them.
//
// It works one agent at a time in the selected practice. The agent comes from ?agent= (Evaluate this agent, a link),
// else the agent last evaluated in this practice (the server says which), else the person chooses one in the agent
// picker drawer. A practice switch drops the agent — it may not belong to the new practice — and finds the new
// practice's last one (MOD-G1); a late answer for the old practice or agent is ignored.
// ─────────────────────────────────────────────────────────────────────────────

export type WorkspaceTab = '' | 'coverage' | 'scenarios' | 'runs' | 'compare' | 'reviews';

export interface WorklistState {
  data: EvaluationWorklist | null;
  /** The first load failed: nothing to show. */
  error: string | null;
  /** A refresh failed: the last good worklist is kept and said to be as old as it is. */
  refreshError: string | null;
  loading: boolean;
  loadedAt: Date | null;
}

export interface WorkspaceContext {
  agentId: number | null;
  agent: AgentDefinition | null;
  agentModel: AgentModelState | null;
  /** Looking up the agent last evaluated in this practice. */
  resolving: boolean;
  resolveError: string | null;
  practiceLabel: string;
  worklist: WorklistState;
  reviewsOpen: number | null;
  reload: () => void;
  chooseAgent: () => void;
  /** Run one scenario, or every active scenario of the agent. */
  openRun: (scenarioId?: string) => void;
  openGenerate: (concern?: string) => void;
  openProposals: () => void;
  openFinding: (resultId: number) => void;
  openModels: () => void;
}

export const useWorkspace = (): WorkspaceContext => useOutletContext<WorkspaceContext>();

const EMPTY: WorklistState = { data: null, error: null, refreshError: null, loading: false, loadedAt: null };
/** While a run is in progress the worklist is read again this often, so its counts follow the run. */
const POLL_MS = 15_000;

const TAB_OF = (pathname: string): WorkspaceTab => {
  const seg = pathname.replace(/^\/evaluation\/?/, '').split('/')[0];
  return (['coverage', 'scenarios', 'runs', 'compare', 'reviews'] as const).find((t) => t === seg) ?? '';
};

const parseId = (v: string | null): number | null => {
  const n = v == null ? NaN : Number(v);
  return Number.isInteger(n) && n > 0 ? n : null;
};

/** The worklist of one agent in the selected practice, with late answers ignored and a failed refresh kept apart. */
function useWorklist(agentId: number | null, merchantId: number | null): [WorklistState, (keep: boolean) => void] {
  const [state, setState] = useState<WorklistState>(EMPTY);
  const seq = useRef(0);
  const load = useCallback(
    (keep: boolean) => {
      const mine = ++seq.current;
      if (agentId == null || merchantId == null) {
        setState(EMPTY);
        return;
      }
      setState((s) => (keep ? { ...s, loading: true } : { ...EMPTY, loading: true }));
      getEvaluationWorklist(agentId)
        .then((data) => {
          if (mine !== seq.current) return;
          setState({ data, error: null, refreshError: null, loading: false, loadedAt: new Date() });
        })
        .catch((err: Error) => {
          if (mine !== seq.current) return;
          setState((s) =>
            keep && s.data ? { ...s, loading: false, refreshError: err.message } : { ...EMPTY, error: err.message },
          );
        });
    },
    [agentId, merchantId],
  );
  useEffect(() => {
    load(false);
  }, [load]);
  const runningJob = state.data?.running?.jobId ?? null;
  useEffect(() => {
    if (runningJob == null) return;
    const t = window.setInterval(() => load(true), POLL_MS);
    return () => window.clearInterval(t);
  }, [runningJob, load]);
  return [state, load];
}

export const EvaluationWorkspace = observer(function EvaluationWorkspace() {
  const { merchantContextStore: ctx } = useStore();
  const merchantId = ctx.selectedMerchantId;
  const practice = merchantId != null ? ctx.merchants.find((m) => m.id === merchantId) : undefined;
  const practiceLabel = practice ? `${practice.title} · #${practice.id}` : merchantId != null ? `Practice #${merchantId}` : 'no practice';
  const location = useLocation();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const tab = TAB_OF(location.pathname);
  const narrow = !Grid.useBreakpoint().md;

  // ── The agent ──────────────────────────────────────────────────────────────
  const urlAgent = parseId(params.get('agent'));
  const [agentId, setAgentId] = useState<number | null>(urlAgent);
  const [resolving, setResolving] = useState(false);
  const [resolveError, setResolveError] = useState<string | null>(null);

  useEffect(() => {
    if (urlAgent != null) setAgentId(urlAgent);
  }, [urlAgent]);

  const setAgent = useCallback(
    (id: number) => {
      setAgentId(id);
      const next = new URLSearchParams(params);
      next.set('agent', String(id));
      setParams(next, { replace: true });
    },
    [params, setParams],
  );

  // A practice switch is a context change: the agent may not belong to the new practice (MOD-G1).
  const shownPractice = useRef(merchantId);
  useEffect(() => {
    if (shownPractice.current === merchantId) return;
    shownPractice.current = merchantId;
    setAgentId(null);
    if (params.has('agent')) {
      const next = new URLSearchParams(params);
      next.delete('agent');
      setParams(next, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [merchantId]);

  // No agent named: open on the one last evaluated in this practice.
  useEffect(() => {
    if (agentId != null || merchantId == null) return;
    let cancelled = false;
    setResolving(true);
    setResolveError(null);
    getLastEvaluatedAgent()
      .then((id) => {
        if (!cancelled && id != null) setAgent(id);
      })
      .catch((err: Error) => !cancelled && setResolveError(err.message))
      .finally(() => !cancelled && setResolving(false));
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [agentId, merchantId]);

  // What the agent is and the model it runs on — shown, never chosen here (D-SI-4).
  const [agent, setAgentFacts] = useState<AgentDefinition | null>(null);
  const [agentModel, setAgentModel] = useState<AgentModelState | null>(null);
  useEffect(() => {
    setAgentFacts(null);
    setAgentModel(null);
    if (agentId == null) return;
    let cancelled = false;
    getAgentById(agentId).then((a) => !cancelled && setAgentFacts(a)).catch(() => undefined);
    getAgentModel(agentId).then((m) => !cancelled && setAgentModel(m)).catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, [agentId, merchantId]);

  const [worklist, load] = useWorklist(agentId, merchantId);

  // The Reviews tab's count: versions waiting for a decision (screen 05's queue).
  const [reviewsOpen, setReviewsOpen] = useState<number | null>(null);
  useEffect(() => {
    setReviewsOpen(null);
    if (agentId == null) return;
    let cancelled = false;
    getScenarioReviewQueue(agentId)
      .then((q) => !cancelled && setReviewsOpen(q.items.filter((i) => i.decision == null).length))
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, [agentId, merchantId, worklist.loadedAt]);

  // ── Drawers ────────────────────────────────────────────────────────────────
  const [picking, setPicking] = useState(false);
  const [run, setRun] = useState<{ open: boolean; scenarioId?: string }>({ open: false });
  const [generate, setGenerate] = useState<{ open: boolean; concern?: string }>({ open: false });
  const [proposals, setProposals] = useState<{ open: boolean; key: string | null }>({ open: false, key: null });
  const [findingId, setFindingId] = useState<number | null>(null);
  const [modelsOpen, setModelsOpen] = useState(false);

  const startRun = async ({ agentId: runAgent, target, key }: RunTargetRequest): Promise<boolean> => {
    try {
      const resp = await enqueueValidationRun({
        agentId: runAgent,
        scenarioId: run.scenarioId,
        triggerSource: run.scenarioId ? 'manual-single' : 'manual',
        merchantId: target.merchantId,
        memberId: target.memberId,
        idempotencyKey: key,
      });
      void message.success(resp.message, 5);
      if (runAgent !== agentId) setAgent(runAgent);
      else load(true);
      return true;
    } catch (err) {
      // The drawer stays open with the same key, so a retry cannot start a second run (review R10).
      void message.error(`The run was not started: ${(err as Error).message} Retry from the drawer; it is the same request.`);
      return false;
    }
  };

  const outlet: WorkspaceContext = useMemo(
    () => ({
      agentId,
      agent,
      agentModel,
      resolving,
      resolveError,
      practiceLabel,
      worklist,
      reviewsOpen,
      reload: () => load(true),
      chooseAgent: () => setPicking(true),
      openRun: (scenarioId) => setRun({ open: true, scenarioId }),
      openGenerate: (concern) => setGenerate({ open: true, concern }),
      openProposals: () => setProposals({ open: true, key: null }),
      openFinding: (resultId) => setFindingId(resultId),
      openModels: () => setModelsOpen(true),
    }),
    [agentId, agent, agentModel, resolving, resolveError, practiceLabel, worklist, reviewsOpen, load],
  );

  const scenarioCount = worklist.data ? worklist.data.counts.library + worklist.data.exploratory.drafts : null;
  const count = (n: number | null) => (n == null ? null : <span className="si-tab-count">{n}</span>);
  const tabs = [
    { key: '', label: 'Overview' },
    { key: 'coverage', label: 'Coverage' },
    { key: 'scenarios', label: <span>Scenarios {count(scenarioCount)}</span> },
    { key: 'runs', label: 'Runs' },
    { key: 'compare', label: 'Compare' },
    { key: 'reviews', label: <span>Reviews {count(reviewsOpen)}</span> },
  ];
  const showActions = tab === '' || tab === 'coverage' || tab === 'reviews';

  // Generate and Run lead where the agent is the subject; the library and the run pages carry their own.
  const actions = (
    <Space wrap size={8}>
      <Button size={narrow ? 'small' : 'middle'} icon={<PlayCircleOutlined />} disabled={agentId == null} onClick={() => setRun({ open: true })}>
        Run scenarios…
      </Button>
      <Button size={narrow ? 'small' : 'middle'} type="primary" icon={<StarOutlined />} disabled={merchantId == null} onClick={() => setGenerate({ open: true })}>
        Generate scenarios
      </Button>
    </Space>
  );

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }} data-testid="evaluation-workspace">
      <style>{WORKSPACE_CSS}</style>
      <PageHeader
        accent="violet"
        icon={<ExperimentOutlined />}
        title="Scenario Intelligence"
        subtitle="Generate, run and review scenarios for any agent. What needs you comes first."
        actions={showActions && !narrow ? actions : undefined}
      />
      {/* On a phone the actions take their own row, so the title is never squeezed out. */}
      {showActions && narrow && actions}
      <Tabs
        className="si-ws-tabs"
        activeKey={tab}
        items={tabs}
        onChange={(key) => navigate(evaluationPath(key as WorkspaceTab, agentId))}
        tabBarStyle={{ marginBottom: 0 }}
      />

      <Outlet context={outlet} />

      {/* Mounted while choosing, so the page itself carries no closed drawer. */}
      {picking && (
        <AgentPickerDrawer
          open={picking}
          onClose={() => setPicking(false)}
          selectedId={agentId}
          onSelect={(id) => id != null && setAgent(id)}
          title="Choose an agent to evaluate"
          allowClear={false}
        />
      )}
      {agentId != null && (
        <RunTargetDrawer open={run.open} onClose={() => setRun({ open: false })} agentId={agentId} scenarioId={run.scenarioId} onRun={startRun} />
      )}
      <GenerateScenariosDrawer
        open={generate.open}
        onClose={() => setGenerate({ open: false })}
        agentId={agentId}
        initialConcern={generate.concern ?? null}
        onOpenProposals={(generatedFor, key) => {
          if (generatedFor !== agentId) setAgent(generatedFor);
          setGenerate({ open: false });
          setProposals({ open: true, key });
        }}
      />
      <ProposalsDrawer
        open={proposals.open}
        onClose={() => {
          setProposals({ open: false, key: null });
          load(true); // an added draft is a new library row
        }}
        agentId={agentId}
        generationKey={proposals.key}
        onGenerateMore={() => {
          setProposals({ open: false, key: null });
          setGenerate({ open: true });
        }}
      />
      <FindingDrawer
        resultId={findingId}
        onClose={() => {
          setFindingId(null);
          load(true); // a finding reviewed in the drawer changes its row
        }}
      />
      <EvaluationModelsDrawer open={modelsOpen} onClose={() => setModelsOpen(false)} />
    </div>
  );
});

const WORKSPACE_CSS = `
.si-tab-count { display: inline-block; min-width: 18px; margin-left: 4px; padding: 0 6px; border-radius: 999px; font-size: 11px; line-height: 16px; text-align: center; background: rgba(100, 116, 139, 0.14); }
`;
